import { createRootRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { Suspense, type ReactNode, useEffect, useRef } from 'react'
import { Toaster } from 'sonner'
import { AuthStateProvider, useAuthState } from '@/lib/auth-state'
import { NotificationProvider } from '@/lib/notifications/use-notifications'
import { AppVersionWatcher } from '@/lib/app-version'
import { useLocale } from '@/i18n/locale'
import { useNoZoom } from '@/lib/no-zoom'
import { JamLoader } from '@/lib/jam/jam-loader'
import { AppErrorPage } from '@/components/route-error'

export const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: AppErrorPage,
})

function RootLayout() {
  useNoZoom()
  return (
    <AuthStateProvider>
      <NotificationProvider>
        <LocaleSync />
        <AppVersionWatcher />
        <JamLoader />
        <Suspense fallback={<div>Loading...</div>}>
          <AuthGate>
            <Outlet />
          </AuthGate>
        </Suspense>
        <Toaster position="bottom-right" />
      </NotificationProvider>
    </AuthStateProvider>
  )
}

/** Apply the language saved on the profile once `me` is known. */
function LocaleSync() {
  const { me } = useAuthState()
  const { locale, setLocale } = useLocale()
  const saved = me?.user.locale ?? null
  useEffect(() => {
    if (saved && saved !== locale) setLocale(saved)
    // Only react to the profile value changing (login, another device's choice).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved])
  return null
}

function AuthGate({ children }: { children: ReactNode }) {
  const { status } = useAuthState()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAuthRoute = pathname.startsWith('/auth')
  const previousStatusRef = useRef(status)

  useEffect(() => {
    const previousStatus = previousStatusRef.current
    previousStatusRef.current = status

    if (status === 'loading') {
      return
    }

    if (status === 'unauthenticated' && !isAuthRoute) {
      if (previousStatus === 'authenticated') {
        window.sessionStorage.setItem('auth_notice', 'stale-session')
      }
      void navigate({ to: '/auth', replace: true })
      return
    }

    if (status === 'authenticated' && isAuthRoute) {
      void navigate({ to: '/', replace: true })
    }
  }, [status, isAuthRoute, navigate])

  if (status === 'loading') {
    return null
  }

  if (status === 'unauthenticated' && !isAuthRoute) {
    return null
  }

  return <>{children}</>
}
